import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import type { UserReview } from '@/features/user-review/constants';
import { OUTCOME_BUCKET } from '@/features/bid-compliance/constants';
import type {
  BidAnalysisState,
  BidComplianceCoverageGroup,
  ComplianceBucket,
  BidComplianceReviewStatus,
  RequirementTiming,
} from '@/features/bid-compliance/types';

const REVIEW_TO_USER_REVIEW: Record<BidComplianceReviewStatus, UserReview> = {
  APPROVED: 'CONFIRMED',
  NEEDS_REVIEW: 'ACTION_REQUIRED',
  REJECTED: 'IGNORED_IMMATERIAL',
};

const USER_REVIEW_TO_REVIEW: Partial<Record<UserReview, BidComplianceReviewStatus>> = {
  CONFIRMED: 'APPROVED',
  ACTION_REQUIRED: 'NEEDS_REVIEW',
  IGNORED_IMMATERIAL: 'REJECTED',
};

export const reviewStatusToComboboxValue = (status: BidComplianceReviewStatus | null): string =>
  status ? REVIEW_TO_USER_REVIEW[status] : 'NONE';

export const comboboxValueToReviewStatus = (
  value: string | null,
): BidComplianceReviewStatus | null =>
  value ? (USER_REVIEW_TO_REVIEW[value as UserReview] ?? null) : null;

const laterRequirementTimings = new Set<RequirementTiming>([
  'BEFORE_CONTRACT_SIGNING',
  'BEFORE_WORK_START',
  'DURING_EXECUTION',
  'AT_HANDOVER',
  'ON_REQUEST',
]);

export const isLaterTiming = (timing?: RequirementTiming | null) =>
  timing != null && laterRequirementTimings.has(timing);

export const isLaterFinding = (
  group: BidComplianceCoverageGroup,
  submissionPhase?: RequirementTiming | null,
) => isLaterTiming(submissionPhase ?? group.requirementTiming);

const isIgnoredFinding = (group: BidComplianceCoverageGroup) =>
  group.reviewStatus === 'REJECTED' || !!group.notRelevant;

export const isOpenFinding = (group: BidComplianceCoverageGroup) => {
  if (group.reviewStatus === 'APPROVED' || isIgnoredFinding(group)) return false;
  return OUTCOME_BUCKET[group.outcome] !== 'covered';
};

export const findingDisplayBucket = (group: BidComplianceCoverageGroup): ComplianceBucket =>
  isOpenFinding(group) ? OUTCOME_BUCKET[group.outcome] : 'covered';

export const findingEvidence = (group: BidComplianceCoverageGroup) => {
  const member = group.members[0];
  const tenderSource = member?.tenderSource;
  const sourceName = tenderSource?.sourceFilename ?? tenderSource?.section ?? null;
  const sourceLabel = [
    sourceName,
    tenderSource?.page != null ? `p. ${tenderSource.page}` : null,
    tenderSource?.section !== sourceName ? tenderSource?.section : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const evidence =
    group.members.find((item) => item.bidDescription) ??
    group.members.find((item) => item.bidSource?.sourceDocId || item.bidSource?.sourceSpan);
  return {
    member,
    tenderSource,
    sourceName,
    sourceLabel,
    bidQuote: evidence?.bidDescription ?? evidence?.bidSource?.sourceSpan ?? null,
    bidSource: evidence?.bidSource ?? null,
  };
};

export const bidAnalysisAlert = (state: BidAnalysisState | null | undefined) => {
  if (!state) return null;
  if (state.refresh.state === 'queued' || state.refresh.state === 'running') return 'checking';
  if (state.refresh.state === 'waiting_for_inputs' || state.refresh.state === 'preparing')
    return 'processing';
  if (state.latestRun?.status === 'not_started' || state.latestRun?.status === 'running')
    return 'checking';
  if (state.refresh.state === 'failed' || state.latestRun?.status === 'failed') return 'failed';
  if (state.freshness === 'stale') return 'stale';
  return null;
};

export const complianceCategoryLabel = (category: string, view: 'client' | 'supplier') => {
  if (category === 'QUALIFICATION_REQUIREMENTS') return t(common.qualification);
  if (category === 'BID_SUBMISSION_REQUIREMENTS')
    return t({
      en: view === 'client' ? 'Submission' : 'Bid submission',
      nb: view === 'client' ? 'Innlevering' : 'Tilbudsinnlevering',
    });
  if (category === 'AWARD_CRITERIA_REQUIREMENTS')
    return t({ en: 'Award criteria', nb: 'Tildelingskriterier' });
  if (category === 'COMMERCIAL_BID_REQUIREMENTS')
    return t({ en: 'Commercial bid', nb: 'Kommersielt tilbud' });
  return t({ en: 'Technical proposal', nb: 'Teknisk tilbud' });
};
